import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Save, Plus, MessageSquarePlus, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const TAG_OPTIONS = ["Cliente", "Lead", "Fornecedor", "VIP", "Urgente", "Parceiro"];

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [phone, setPhone] = useState("");
  const [starting, setStarting] = useState(null);
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", cnpj: "", notes: "", tags: [], regime_tributario: "" });

  const load = async () => {
    const { data } = await api.get("/contacts");
    setContacts(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search) return contacts;
    const q = search.toLowerCase();
    const digits = search.replace(/\D/g, "");
    return contacts.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").includes(q) ||
      (c.cnpj || "").includes(q) ||
      (digits && (c.cnpj || "").replace(/\D/g, "").includes(digits))
    );
  }, [contacts, search]);

  const openEdit = (c) => {
    setCreating(false);
    setEditing(c);
    setForm({ name: c.name || "", cnpj: c.cnpj || "", notes: c.notes || "", tags: c.tags || [], regime_tributario: c.regime_tributario || "" });
  };

  const openNew = () => {
    setCreating(true);
    setPhone("");
    setForm({ name: "", cnpj: "", notes: "", tags: [], regime_tributario: "" });
    setEditing({});
  };

  const startChat = async (c) => {
    setStarting(c.phone);
    try {
      const { data } = await api.post("/chats/start", { phone: c.phone });
      navigate(`/inbox/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível iniciar a conversa");
    } finally { setStarting(null); }
  };

  const toggleTag = (t) => {
    setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));
  };

  const save = async (thenChat = false) => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    try {
      let saved;
      if (creating) {
        ({ data: saved } = await api.post("/contacts", { ...form, phone }));
        toast.success("Contato criado");
      } else {
        ({ data: saved } = await api.patch(`/contacts/${editing.phone}`, form));
        toast.success("Contato atualizado");
      }
      setEditing(null);
      load();
      if (thenChat) startChat(saved);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    }
  };

  return (
    <div className="h-full overflow-auto p-6 lg:p-10">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="heading text-3xl font-extrabold">Contatos</h1>
            <p className="text-sm text-muted-foreground mt-1">Cadastre clientes e inicie conversas pelo WhatsApp.</p>
          </div>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="contact-new-button">
            <Plus className="w-4 h-4 mr-2" /> Novo contato
          </Button>
        </div>

        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 className="pl-9 h-10" placeholder="Buscar por nome, telefone ou CNPJ" />
        </div>

        <Card>
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">
                Nenhum contato encontrado. Contatos são criados automaticamente ao receber mensagens.
              </div>
            ) : filtered.map((c) => (
              <div key={c.phone} data-testid="contact-row"
                      className="w-full flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 p-4 border-b border-border hover:bg-muted/60 transition-colors text-left">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100">
                    {(c.name || c.phone).slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{c.name || c.phone}</div>
                  <div className="text-xs text-muted-foreground mono">+{c.phone} {c.cnpj ? `· CNPJ ${c.cnpj}` : ""}</div>
                </div>
                <div className="hidden sm:flex flex-wrap gap-1 justify-end">
                  {(c.tags || []).map((t) => (
                    <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                  ))}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid="contact-edit-button">
                    <Pencil className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Editar</span>
                  </Button>
                  <Button size="sm" onClick={() => startChat(c)} disabled={starting === c.phone}
                          className="bg-emerald-600 hover:bg-emerald-700" data-testid="contact-start-chat-button">
                    {starting === c.phone ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquarePlus className="w-4 h-4 sm:mr-1" />}
                    <span className="hidden sm:inline">Conversar</span>
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{creating ? "Novo contato" : "Editar contato"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {creating ? (
              <div>
                <Label>Telefone WhatsApp (DDD + número)</Label>
                <Input data-testid="contact-phone-field" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="48 99999-9999" />
              </div>
            ) : (
              <div className="text-xs text-muted-foreground mono">+{editing?.phone}</div>
            )}
            <div>
              <Label>Nome</Label>
              <Input data-testid="contact-name-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>CNPJ / CPF</Label>
              <Input data-testid="contact-cnpj-field" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} placeholder="00.000.000/0000-00" />
            </div>
            <div>
              <Label>Categoria</Label>
              <Input data-testid="contact-regime-select" className="mt-1" value={form.regime_tributario}
                     onChange={(e) => setForm({ ...form, regime_tributario: e.target.value })} placeholder="Ex.: Varejo, Atacado, Pessoa física" />
            </div>
            <div>
              <Label>Etiquetas</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {TAG_OPTIONS.map((t) => {
                  const active = form.tags.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleTag(t)}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              active
                                ? "bg-emerald-600 text-white border-emerald-600"
                                : "bg-transparent border-border hover:bg-muted"
                            }`}>{t}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Observações internas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => save(true)} data-testid="contact-save-chat-button"><MessageSquarePlus className="w-4 h-4 mr-1" /> Salvar e conversar</Button>
            <Button onClick={() => save()} data-testid="contact-save-button" className="bg-emerald-600 hover:bg-emerald-700"><Save className="w-4 h-4 mr-1" /> Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
